import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CustomerAuthContext = createContext(null);
const TOKEN_KEY = "frida_customer_token";

// Storefront müşteri hesabı (giriş/kayıt) durumunu tutar. Panel/personel
// oturumlarından tamamen ayrıdır — bu yalnızca site ziyaretçisinin kendi
// hesabı için kullanılır. Token her zaman "X-Customer-Token" başlığında
// taşınır (bkz. backend core/customer_auth.php) — "Authorization" başlığı
// bu uçlarda ZEO Connect anahtarı için ayrılmıştır.
export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // localStorage kullanılamıyor (gizli sekme vb.) — sessizce geç
    }
    setToken(null);
    setCustomer(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch("/api/customer-me.php", {
          headers: { "X-Customer-Token": token },
        });
        const data = await res.json().catch(() => null);
        if (cancelled) return;

        if (res.ok && data?.customer) {
          setCustomer(data.customer);
        } else {
          clearSession();
        }
      } catch {
        // Ağ hatası — oturumu bozma, sonraki denemede tekrar dener.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, clearSession]);

  const persistSession = useCallback((newToken, newCustomer) => {
    try {
      localStorage.setItem(TOKEN_KEY, newToken);
    } catch {
      // localStorage kullanılamıyor — token yalnızca bellekte tutulur,
      // sayfa yenilenince oturum kaybolur ama işlem başarısız olmaz.
    }
    setToken(newToken);
    setCustomer(newCustomer);
  }, []);

  const register = useCallback(async ({ name, email, phone, password }) => {
    try {
      const res = await fetch("/api/customer-register.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, password }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.status !== "success") {
        return { ok: false, error: data?.message || "Kayıt işlemi başarısız oldu." };
      }
      persistSession(data.token, data.customer);
      return { ok: true };
    } catch {
      return { ok: false, error: "Bağlantı sorunu — lütfen tekrar deneyin." };
    }
  }, [persistSession]);

  const login = useCallback(async ({ email, password }) => {
    try {
      const res = await fetch("/api/customer-login.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.status !== "success") {
        return { ok: false, error: data?.message || "Giriş başarısız oldu." };
      }
      persistSession(data.token, data.customer);
      return { ok: true };
    } catch {
      return { ok: false, error: "Bağlantı sorunu — lütfen tekrar deneyin." };
    }
  }, [persistSession]);

  const logout = useCallback(() => clearSession(), [clearSession]);

  const fetchOrders = useCallback(async () => {
    if (!token) return { ok: false, error: "Giriş yapmanız gerekiyor." };
    try {
      const res = await fetch("/api/customer-orders.php", {
        headers: { "X-Customer-Token": token },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) return { ok: false, error: data?.message || "Sipariş geçmişi alınamadı." };
      return { ok: true, orders: data?.orders || [] };
    } catch {
      return { ok: false, error: "Bağlantı sorunu — lütfen tekrar deneyin." };
    }
  }, [token]);

  return (
    <CustomerAuthContext.Provider
      value={{ customer, token, loading, isLoggedIn: !!customer, register, login, logout, fetchOrders }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used within a CustomerAuthProvider");
  return ctx;
}
